import React, { useRef, useEffect, useState } from 'react'
import styled from 'styled-components'
import { 
  Home, Scan, FileText, Database, Link2, Settings, Info, Lock, Unlock, Radar, HelpCircle, Server, ChevronRight, Shield, Globe, Wifi, Key, Layers, FileType, MapPin, Network, Zap, Search, Users 
} from 'lucide-react'
import { useNavigate, Link } from 'react-router-dom'
import Cookies from 'js-cookie'
import { apiRequest } from '../utils/api'

const TRANSITION_DURATION = '0.3s'
const TRANSITION_EASING = 'ease'

const SidebarContainer = styled.aside`
  position: fixed;
  top: 0;
  left: 0;
  width: ${props => (props.isExpanded ? '270px' : 'calc(64px + 0.5rem)')};
  height: 100vh;
  background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
  color: #fff;
  padding: 1rem 0.75rem 3.5rem 0.75rem;
  transition: width ${TRANSITION_DURATION} ${TRANSITION_EASING};
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 40;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(77, 169, 255, 0.5);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(77, 169, 255, 0.7);
  }
`

const LogoContainer = styled.div`
  position: relative;
  height: 42px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`

const LogoImage = styled.img`
  width: 180px;
  margin-left: 0.25rem;
  object-fit: contain;
  transition: opacity ${TRANSITION_DURATION} ${TRANSITION_EASING}, transform ${TRANSITION_DURATION} ${TRANSITION_EASING};
  opacity: ${props => (props.isExpanded ? 1 : 0)};
  transform: ${props => props.isExpanded ? 'translateX(0)' : 'translateX(-20px)'};
  position: absolute;
`

const ShieldIcon = styled.img`
  height: 38px;
  width: 38px;
  position: absolute;
  left: 0.25rem;
  transition: opacity ${TRANSITION_DURATION} ${TRANSITION_EASING}, transform ${TRANSITION_DURATION} ${TRANSITION_EASING};
  opacity: ${props => (props.isExpanded ? 0 : 1)};
  transform: ${props => props.isExpanded ? 'scale(0.8)' : 'scale(1)'};
`

const MainContent = styled.main`
  margin-left: ${props =>
    props.sidebarExpanded ? '270px' : 'calc(64px + 0.5rem)'};
  transition: margin-left ${TRANSITION_DURATION} ${TRANSITION_EASING};
  min-height: 100vh;
`

const LockButton = styled.button`
  position: fixed;
  bottom: 1.5rem;
  left: ${props => (props.isExpanded ? '270px' : 'calc(64px + 0.5rem)')};
  margin-left: -40px;
  width: 20px;
  height: 20px;
  background: transparent;
  border: none;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s ease, left ${TRANSITION_DURATION} ${TRANSITION_EASING};
  z-index: 1000;
  opacity: 0.7;

  &:hover {
    color: #b7e0ff;
    opacity: 1;
  }
`

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const NavButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  color: rgb(255, 255, 255);
  border: none;
  border-radius: 0.5rem;
  background-color: ${props =>
    props.active ? 'rgba(0, 87, 204, 0.5)' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  font-size: 0.875rem;
  white-space: nowrap;
  &:hover {
    background-color: rgba(0, 87, 204, 0.7);
  }
  svg {
    min-width: 18px;
    width: 18px;
    height: 18px;
  }
  span {
    opacity: ${props => (props.isExpanded ? 1 : 0)};
    transition: opacity 0.3s ease;
    overflow: hidden;
  }
`

const DropdownContainer = styled.div`
  position: relative;
`

const DropdownToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  color: rgb(255, 255, 255);
  border: none;
  border-radius: 0.5rem;
  background-color: ${props =>
    props.active ? 'rgba(0, 87, 204, 0.5)' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  font-size: 0.875rem;
  white-space: nowrap;
  &:hover {
    background-color: rgba(0, 87, 204, 0.7);
  }
  svg {
    min-width: 18px;
    width: 18px;
    height: 18px;
  }
  .label {
    opacity: ${props => (props.isExpanded ? 1 : 0)};
    transition: opacity 0.3s ease;
    overflow: hidden;
    flex: 1;
    text-align: left;
  }
  .chevron {
    opacity: ${props => (props.isExpanded ? 1 : 0)};
    transition: all 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(90deg)' : 'rotate(0deg)'};
    min-width: 14px;
    width: 14px;
    height: 14px;
  }
`

const DropdownMenu = styled.div`
  max-height: ${props => props.isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: max-height ${TRANSITION_DURATION} ${TRANSITION_EASING};
  background-color: rgba(0, 65, 159, 0.3);
  border-radius: 0.5rem;
  margin-top: 0.25rem;
  margin-left: ${props => props.isExpanded ? '1rem' : '0'};
  opacity: ${props => props.isExpanded ? 1 : 0};
`

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.4rem 0.75rem;
  color: rgba(255, 255, 255, 0.9);
  border: none;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  font-size: 0.8rem;
  white-space: nowrap;
  &:hover {
    background-color: rgba(77, 169, 255, 0.3);
    color: #fff;
  }
  svg {
    min-width: 14px;
    width: 14px;
    height: 14px;
  }
`

const DropdownItemButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.4rem 0.75rem;
  color: rgba(255, 255, 255, 0.9);
  border: none;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  font-size: 0.8rem;
  white-space: nowrap;
  &:hover {
    background-color: rgba(77, 169, 255, 0.3);
    color: #fff;
  }
  svg {
    min-width: 14px;
    width: 14px;
    height: 14px;
  }
`

const SecurityScoreBox = styled.div`
  margin-top: 2rem;
  padding: 0.75rem;
  background-color: rgba(0, 87, 204, 0.19);
  border-radius: 0.5rem;
  transition: all ${TRANSITION_DURATION} ${TRANSITION_EASING};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: ${props => (props.isExpanded ? 'stretch' : 'center')};
`

const ScoreHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  opacity: ${props => (props.isExpanded ? 1 : 0)};
  height: ${props => (props.isExpanded ? 'auto' : '0')};
  transition: all ${TRANSITION_DURATION} ${TRANSITION_EASING};
  overflow: hidden;
`

const ScoreTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: rgb(255, 255, 255);
  margin: 0;
`

const ScoreFlex = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${props => (props.isExpanded ? 'space-between' : 'center')};
  width: 100%;
`

const ScoreValue = styled.div`
  font-size: ${props => (props.isExpanded ? '1.25rem' : '1rem')};
  font-weight: bold;
  color: #4da9ff;
  white-space: nowrap;
`

const ScoreChange = styled.span`
  font-size: 0.75rem;
  color: rgb(74, 222, 128);
  opacity: ${props => (props.isExpanded ? 1 : 0)};
  transition: opacity ${TRANSITION_DURATION} ${TRANSITION_EASING};
`

const ProgressBarContainer = styled.div`
  width: 100%;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 9999px;
  height: ${props => (props.isExpanded ? '0.25rem' : '0')};
  margin-top: 0.5rem;
  transition: all ${TRANSITION_DURATION} ${TRANSITION_EASING};
  overflow: hidden;
`

const ProgressBar = styled.div`
  background-color: #4da9ff;
  height: 100%;
  border-radius: 9999px;
  width: ${props => props.width || '0%'};
  transition: width ${TRANSITION_DURATION} ${TRANSITION_EASING};
`

const InfoIconWrapper = styled.div`
  position: relative;
  display: inline-flex;
  cursor: pointer;
  .tooltip {
    position: fixed;
    left: 280px;
    transform: translateY(-50%);
    visibility: hidden;
    opacity: 0;
    transition: opacity ${TRANSITION_DURATION} ${TRANSITION_EASING};
  }
  &:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }
`

const Tooltip = styled.div`
  width: 220px;
  background-color: #00419f;
  color: #fff;
  text-align: left;
  border-radius: 4px;
  padding: 8px;
  z-index: 1000;
  pointer-events: none;
  font-size: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  &::before {
    content: "";
    position: absolute;
    top: 50%;
    right: 100%;
    margin-top: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: transparent #00419f transparent transparent;
  }
`

const TooltipSection = styled.div`
  margin-bottom: 0.5rem;
  &:last-child {
    margin-bottom: 0;
  }
`

const TooltipTitle = styled.div`
  font-weight: bold;
  margin-bottom: 0.25rem;
`

const TooltipText = styled.div`
  line-height: 1.2;
`

const ZDOLogo = "/zeroday-ops.png";
const ShieldLogoPath = "/zeroday-shield.png";

const calculateSecurityScore = severityCounts => {
  const { Critical = 0, High = 0, Medium = 0, Low = 0, Info = 0 } = severityCounts
  const penalty = Critical * 10 + High * 5 + Medium * 2 + Low * 1 + Info * 0
  let score = 100 - penalty
  if (score < 0) score = 0
  else if (score > 100) score = 100
  return score
}

const Sidebar = ({ onToggle }) => {
  const [activeTab, setActiveTab] = useState('overview')
  const [securityScore, setSecurityScore] = useState(100)
  const [scoreDelta, setScoreDelta] = useState(0)
  const [isLocked, setIsLocked] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [dropdownStates, setDropdownStates] = useState({})
  const sidebarRef = useRef(null)
  const navigate = useNavigate()

  const navItems = [
    { icon: Home, label: 'Dashboard', id: 'dashboard', url: '/' },
    {
      icon: Shield,
      label: 'Penetration Testing',
      id: 'penetration-testing',
      hasDropdown: true,
      dropdownItems: [
        { icon: Radar, label: 'Automated Scan', id: 'automated-scan', url: '/autoscan' },
        { icon: Scan, label: 'Advanced Scan', id: 'advanced-scan', url: '/advancedscan' },
      ]
    },
    // {
    //   icon: Server,
    //   label: 'Vulnerability Assessment',
    //   id: 'vulnerability-assessment',
    //   hasDropdown: true,
    //   dropdownItems: [
    //     { icon: Globe, label: 'External', id: 'external-vuln', onClick: () => alert('External vulnerability assessment coming soon!') },
    //     { icon: Network, label: 'Internal', id: 'internal-vuln', url: '/vulnerability-assessment' },
    //   ]
    // },
    {
      icon: Search,
      label: 'Reconnaissance',
      id: 'reconnaissance',
      url: '/reconnaissance',
      hasDropdown: false,
      dropdownItems: [
        { icon: Globe, label: 'Domain', id: 'recon-domain', url: '/recon/domain' },
        { icon: Wifi, label: 'IP Details', id: 'recon-ip', url: '/recon/ip' },
        { icon: Key, label: 'SSL Details', id: 'recon-ssl', url: '/recon/ssl' },
        { icon: Layers, label: 'Tech Stack', id: 'recon-tech', url: '/recon/tech' },
        { icon: FileType, label: 'Response Headers', id: 'recon-headers', url: '/recon/headers' },
        { icon: MapPin, label: 'Traceroute', id: 'recon-traceroute', url: '/recon/traceroute' },
        { icon: Network, label: 'Subdomains', id: 'recon-subdomains', url: '/recon/subdomains' },
        { icon: Zap, label: 'Open Ports', id: 'recon-ports', url: '/recon/ports' },
        { icon: Info, label: 'Whois', id: 'recon-whois', url: '/recon/whois' },
        { icon: Search, label: 'Dig', id: 'recon-dig', url: '/recon/dig' },
      ]
    },
    {
      icon: FileText,
      label: 'Reports',
      id: 'reports',
      hasDropdown: true,
      dropdownItems: [
        { icon: Radar, label: 'Automated', id: 'reports-automated', url: '/automated-reports' },
        { icon: Scan, label: 'Advanced', id: 'reports-advanced', url: '/advanced-reports' },
        // { icon: Network, label: 'Internal VA', id: 'reports-internal', url: '/reports/internal' },
      ]
    },
    {
      icon: Database,
      label: 'Assets',
      id: 'assets',
      url: '/add-assets',
      hasDropdown: true,
      dropdownItems: [
        { icon: Globe, label: 'Web', id: 'assets-web', url: '/add-assets/web' },
        { icon: Zap, label: 'API', id: 'assets-api', url: '/add-assets/api', disabled: true },
        { icon: Network, label: 'Network', id: 'assets-network', url: '/add-assets/network',  disabled: true},
        { icon: Shield, label: 'Firewall', id: 'assets-firewall', url: '/add-assets/firewall'},
      ]
    },
    { icon: Link2, label: 'Integration', id: 'integration', url: '/integration', disabled: true },
    { icon: Users, label: 'Teams', id: 'teams', url: '/teams' },
    { icon: Settings, label: 'Settings', id: 'settings', url: '/settings' },
    { 
      icon: HelpCircle, 
      label: 'Help', 
      id: 'help', 
      external: true,
      hasDropdown: true,
      dropdownItems: [
        {
          icon: Info,
          label: 'Getting Started',
          id: 'getting-started',
          onClick: () => window.open('https://help.zerodayops.com', '_blank')
        }
      ]
    },
  ]

  useEffect(() => {
    const lockedCookie = Cookies.get('sidebarLocked')
    if (lockedCookie === 'true') {
      setIsLocked(true)
      setIsExpanded(true)
    } else {
      setIsLocked(false)
      setIsExpanded(false)
    }
  }, [])

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const res = await apiRequest('/api/auth/overview', { method: 'GET' })
        if (!res.ok) {
          throw new Error(`Failed to fetch overview. Status: ${res.status}`)
        }
        const data = await res.json()
        setSecurityScore(calculateSecurityScore(data))
      } catch (err) {
        console.error('Error fetching overview:', err)
      }
    }
    fetchOverview()
  }, [])

  const handleMouseEnter = () => {
    if (!isLocked) {
      setIsExpanded(true)
    }
  }

  const handleMouseLeave = () => {
    if (!isLocked) {
      setIsExpanded(false)
    }
  }

  const toggleLock = () => {
    const newLockState = !isLocked
    setIsLocked(newLockState)
    if (newLockState) {
      setIsExpanded(true)
    }
    Cookies.set('sidebarLocked', newLockState ? 'true' : 'false', { expires: 7 })
  }

  const toggleDropdown = (itemId) => {
    setDropdownStates(prev => {
      const newState = {}
      Object.keys(prev).forEach(key => {
        newState[key] = key === itemId ? !prev[key] : false
      })
      if (!prev[itemId]) {
        newState[itemId] = true
      }
      return newState
    })
  }

  const handleDropdownItemClick = (item) => {
    if (item.onClick) {
      item.onClick()
    } else if (item.url) {
      navigate(item.url)
    }
    setActiveTab(item.id)
  }

  useEffect(() => {
    if (onToggle) {
      onToggle(isExpanded)
    }
  }, [isExpanded, onToggle])

  const renderNavItem = (item) => {
    // Check if item is disabled
    if (item.disabled) {
      return (
        <NavButton
          key={item.id}
          onClick={() => {}}
          active={activeTab === item.id}
          to="#"
          isExpanded={isExpanded}
          style={{ 
            opacity: 0.5, 
            cursor: 'not-allowed',
            pointerEvents: 'none'
          }}
        >
          <item.icon />
          <span>{item.label}</span>
        </NavButton>
      )
    }

    if (item.hasDropdown) {
      const isOpen = dropdownStates[item.id] || false

      // Handle disabled dropdown items
      const renderDropdownItem = (subItem) => {
        if (subItem.disabled) {
          return (
            <DropdownItemButton
              key={subItem.id}
              onClick={() => {}}
              style={{ 
                opacity: 0.5, 
                cursor: 'not-allowed',
                pointerEvents: 'none'
              }}
            >
              <subItem.icon />
              {subItem.label}
            </DropdownItemButton>
          )
        }

        if (subItem.url) {
          return (
            <DropdownItem
              key={subItem.id}
              to={subItem.url}
              onClick={() => setActiveTab(subItem.id)}
            >
              <subItem.icon />
              {subItem.label}
            </DropdownItem>
          )
        } else {
          return (
            <DropdownItemButton
              key={subItem.id}
              onClick={() => handleDropdownItemClick(subItem)}
            >
              <subItem.icon />
              {subItem.label}
            </DropdownItemButton>
          )
        }
      }

      // Help menu special case
      if (item.id === 'help') {
        return (
          <DropdownContainer key={item.id}>
            <DropdownToggle
              isExpanded={isExpanded}
              isOpen={isOpen}
              onClick={() => toggleDropdown(item.id)}
              active={activeTab === item.id}
            >
              <item.icon />
              <span className="label">{item.label}</span>
              <ChevronRight className="chevron" />
            </DropdownToggle>
            <DropdownMenu isOpen={isOpen && isExpanded} isExpanded={isExpanded}>
              {item.dropdownItems.map(renderDropdownItem)}
            </DropdownMenu>
          </DropdownContainer>
        )
      }

      if (item.url) {
        return (
          <DropdownContainer key={item.id}>
            <DropdownToggle
              isExpanded={isExpanded}
              isOpen={isOpen}
              onClick={() => {
                navigate(item.url)
                setActiveTab(item.id)
                toggleDropdown(item.id)
              }}
              active={activeTab === item.id}
            >
              <item.icon />
              <span className="label">{item.label}</span>
              <ChevronRight className="chevron" />
            </DropdownToggle>
            <DropdownMenu isOpen={isOpen && isExpanded} isExpanded={isExpanded}>
              {item.dropdownItems.map(renderDropdownItem)}
            </DropdownMenu>
          </DropdownContainer>
        )
      }

      return (
        <DropdownContainer key={item.id}>
          <DropdownToggle
            isExpanded={isExpanded}
            isOpen={isOpen}
            onClick={() => toggleDropdown(item.id)}
            active={activeTab === item.id}
          >
            <item.icon />
            <span className="label">{item.label}</span>
            <ChevronRight className="chevron" />
          </DropdownToggle>
          <DropdownMenu isOpen={isOpen && isExpanded} isExpanded={isExpanded}>
            {item.dropdownItems.map(renderDropdownItem)}
          </DropdownMenu>
        </DropdownContainer>
      )
    }

    return (
      <NavButton
        key={item.id}
        onClick={() => setActiveTab(item.id)}
        active={activeTab === item.id}
        to={item.url}
        isExpanded={isExpanded}
        target={item.external ? '_blank' : undefined}
        rel={item.external ? 'noopener noreferrer' : undefined}
      >
        <item.icon />
        <span>{item.label}</span>
      </NavButton>
    )
  }

  return (
    <>
      <SidebarContainer
        ref={sidebarRef}
        isExpanded={isExpanded}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <LogoContainer>
          <ShieldIcon 
            src={ShieldLogoPath} 
            alt="Zeroday Ops Shield" 
            isExpanded={isExpanded} 
          />
          <LogoImage 
            src={ZDOLogo} 
            alt="Zeroday Ops Logo" 
            isExpanded={isExpanded} 
          />
        </LogoContainer>
        <br/>
        <Nav>
          {navItems.map(renderNavItem)}
        </Nav>

        <SecurityScoreBox isExpanded={isExpanded}>
          <ScoreHeader isExpanded={isExpanded}>
            <ScoreTitle>Security Score</ScoreTitle>
            <InfoIconWrapper>
              <Info size={16} color="#4DA9FF" />
              <Tooltip className="tooltip">
                <TooltipSection>
                  <TooltipTitle>Your Current Score</TooltipTitle>
                  <TooltipText>
                    The score shows your security rating out of 100. Higher numbers indicate better security.
                  </TooltipText>
                </TooltipSection>
                <TooltipSection>
                  <TooltipTitle>Score Changes</TooltipTitle>
                  <TooltipText>
                    An up arrow (↑) shows improvement, while a down arrow (↓) indicates a decrease.
                  </TooltipText>
                </TooltipSection>
                <TooltipSection>
                  <TooltipTitle>Progress Bar</TooltipTitle>
                  <TooltipText>
                    The colored bar fills based on your score.
                  </TooltipText>
                </TooltipSection>
              </Tooltip>
            </InfoIconWrapper>
          </ScoreHeader>
          <ScoreFlex isExpanded={isExpanded}>
            <ScoreValue isExpanded={isExpanded}>
              {isExpanded ? `${securityScore}/100` : securityScore}
            </ScoreValue>
            {scoreDelta !== 0 && (
              <ScoreChange isExpanded={isExpanded}>
                {scoreDelta > 0 ? `↑ ${scoreDelta}` : `↓ ${Math.abs(scoreDelta)}`}
              </ScoreChange>
            )}
          </ScoreFlex>
          <ProgressBarContainer isExpanded={isExpanded}>
            <ProgressBar width={`${securityScore}%`} />
          </ProgressBarContainer>
        </SecurityScoreBox>

        <LockButton onClick={toggleLock} isExpanded={isExpanded} aria-label="Toggle sidebar lock">
          {isLocked ? <Lock size={20} /> : <Unlock size={20} />}
        </LockButton>
      </SidebarContainer>

      <MainContent sidebarExpanded={isExpanded}>
        {/* Your main content goes here */}
      </MainContent>
    </>
  )
}

export default Sidebar
